import {
  Avatar,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import { CSSProperties, FC } from 'react';

import { PieceAssetSize } from '@apiContract';
import { Label } from '@components/Label';
import { SentIcon } from 'src/assets/icons/SentIcon';

import { getPieceAsset, isPieceSend } from '@utils/piece';

import { getCapturePieceStatus, getPieceDetection, getPieceRecipientParam } from '../../../../helper';
import { CaptureId, CapturePiece, ColorStatus, RecipientStatus } from '../../../../types';
import {
  sxAvatar,
  sxAvatarBase,
  sxDisabled,
  sxImg,
  sxPiece,
  sxPieceItemBtn,
  sxPieceItemBtnActive,
  sxSendIcon,
  sxSkeleton,
} from '../../styles';

type ItemProps = {
  handleClick: (cursor: CaptureId, capturePiece: CapturePiece) => void;
  style: CSSProperties;
  piece: CapturePiece;
  isActive: boolean;
};

const Item: FC<ItemProps> = ({ handleClick, style, isActive, piece }) => {
  const pieceDetection = getPieceDetection(piece);

  const status = getCapturePieceStatus(piece);
  const isFailed = piece.status === 'failed';

  const isSend = isPieceSend(piece?.piece);

  return (
    <>
      {piece.piece || piece.status === 'failed' ? (
        <ListItem disablePadding style={style} component="div" sx={sxPiece}>
          <ListItem
            onClick={() => handleClick(piece?.id || '', piece)}
            sx={isActive ? sxPieceItemBtnActive : sxPieceItemBtn}
            data-testid="rd-review-piece-list-item"
          >
            <ListItemAvatar sx={{ position: 'relative' }}>
              <Avatar
                alt="Piece"
                src={
                  getPieceAsset(piece.piece?.pieceAssets || [], PieceAssetSize.Small)?.url ||
                  (piece.photo instanceof File ? URL.createObjectURL(piece.photo) : piece.photo)
                }
                variant="rounded"
                imgProps={{ sx: sxImg }}
                sx={{ ...sxAvatar, ...(isSend ? sxDisabled : {}) }}
              />
              {isSend ? <SentIcon sx={sxSendIcon} /> : null}
            </ListItemAvatar>
            <ListItemText
              sx={isSend ? sxDisabled : {}}
              primary={
                <Typography variant="subtitle2" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden">
                  {getPieceRecipientParam(piece.piece, pieceDetection, 'displayName')}
                </Typography>
              }
              secondary={
                <Typography
                  color="text.secondary"
                  variant="body2"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  overflow="hidden"
                >
                  {getPieceRecipientParam(piece.piece, pieceDetection, 'userName')}
                </Typography>
              }
            />
            <Box sx={{ ml: 2 }}>
              <Label
                sx={isSend ? sxDisabled : {}}
                variant="soft"
                color={isFailed ? 'error' : ColorStatus[status as RecipientStatus]}
              >
                {isFailed ? 'failed' : status}
              </Label>
            </Box>
          </ListItem>
        </ListItem>
      ) : (
        <Box display="flex" style={style} sx={sxSkeleton} alignItems="center" gap={2}>
          <Skeleton variant="rounded" sx={sxAvatarBase} />
          <Box flex={1}>
            <Skeleton variant="text" width="75%" />
            <Skeleton variant="text" width="55%" />
          </Box>
          <Skeleton variant="rounded" width={64} height={24} />
        </Box>
      )}
    </>
  );
};

export default Item;
